import { type ProjectSolarPotential } from '@/api/queries/project/useGetProjectSolarPotential';
import { type SolarDesignDraftState } from '@payaca/helpers/solarDesignDraft';

export type RecoverySite = {
  id: string;
  coordinates?: { latitude: number; longitude: number } | null;
};

export type SolarDesignRecoverySource = {
  source: ProjectSolarPotential | null;
  site?: RecoverySite;
};

type PanelDimensions = {
  widthMeters: number;
  heightMeters: number;
  capacityWatts: number;
};

/**
 * What a layout was drawn against. Recovery carries it only to an analysis of
 * the same site and pin with the same module size; anything else is a hard stop.
 */
export type SolarDesignRecoveryBaseline =
  | {
      known: true;
      site?: RecoverySite;
      /** Required to match when set: the analysis record the draft names. */
      sourceAnalysisId?: string | null;
      /** Required to match when set: the analysed building centre. */
      centre?: { lat: number; lng: number } | null;
      panelDimensions?: PanelDimensions | null;
    }
  | { known: false; reason: string };

export const SITE_CHANGED_MESSAGE =
  'The site address or pin no longer matches the one this layout was saved against, so it cannot be carried to the current roof analysis automatically.';
export const PANEL_SIZE_CHANGED_MESSAGE =
  'The panel size in the current roof analysis no longer matches the one this layout was drawn with, so it cannot be carried across automatically.';
export const SOURCE_NOT_RECORDED_MESSAGE =
  'The site or panel size this layout was saved with was not recorded, so it cannot be checked against the current roof analysis.';
const KEPT = ' Your layout is still here and has not been replaced.';

const sameSite = (a?: RecoverySite | null, b?: RecoverySite | null) =>
  !!a?.coordinates &&
  !!b?.coordinates &&
  a.id === b.id &&
  a.coordinates.latitude === b.coordinates.latitude &&
  a.coordinates.longitude === b.coordinates.longitude;

const sameDimensions = (
  a?: PanelDimensions | null,
  b?: PanelDimensions | null
) =>
  !!a &&
  !!b &&
  a.widthMeters === b.widthMeters &&
  a.heightMeters === b.heightMeters &&
  a.capacityWatts === b.capacityWatts;

/** The module size every analysed roof in the layout was generated with. */
export const layoutPanelDimensions = (
  state: SolarDesignDraftState
): PanelDimensions | null => {
  const recorded = state.layout.workingFaces.flatMap((face) =>
    face.generation?.panelDimensions ? [face.generation.panelDimensions] : []
  );
  const [first] = recorded;
  return first && recorded.every((dims) => sameDimensions(dims, first))
    ? {
        widthMeters: first.widthMeters,
        heightMeters: first.heightMeters,
        capacityWatts: first.capacityWatts,
      }
    : null;
};

/** The analysis the editor opened with, when the conflict arises mid-session. */
export const sessionRecoveryBaseline = (
  source: ProjectSolarPotential,
  site?: RecoverySite
): SolarDesignRecoveryBaseline => ({
  known: true,
  site,
  centre: source.centre ?? null,
  panelDimensions: source.geometry?.panelDimensions ?? null,
});

/**
 * A draft that is already stale when the page opens: the baseline is what the
 * draft itself recorded, never the analysis the page happened to load.
 */
export const savedDraftRecoveryBaseline = (input: {
  state?: SolarDesignDraftState;
  sourceAnalysisId?: string | null;
  sourceSite?: RecoverySite | null;
  staleReason?: 'ANALYSIS_REFRESHED' | 'SITE_CHANGED' | null;
  currentSite?: RecoverySite;
  currentSource: ProjectSolarPotential;
}): SolarDesignRecoveryBaseline => {
  if (input.staleReason === 'SITE_CHANGED')
    return { known: false, reason: SITE_CHANGED_MESSAGE };
  const panelDimensions = input.state
    ? layoutPanelDimensions(input.state)
    : null;
  if (
    !input.sourceAnalysisId ||
    !input.sourceSite?.coordinates ||
    !panelDimensions
  )
    return { known: false, reason: SOURCE_NOT_RECORDED_MESSAGE };
  if (
    !sameSite(input.sourceSite, input.currentSite) ||
    input.currentSource.sourceAnalysisId !== input.sourceAnalysisId
  )
    return { known: false, reason: SITE_CHANGED_MESSAGE };
  if (
    !sameDimensions(
      panelDimensions,
      input.currentSource.geometry?.panelDimensions
    )
  )
    return { known: false, reason: PANEL_SIZE_CHANGED_MESSAGE };
  return {
    known: true,
    site: input.sourceSite,
    sourceAnalysisId: input.sourceAnalysisId,
    panelDimensions,
  };
};

/** Read first, then save the captured layout against its original revision. */
export const recoverSolarDesign = async <T>(input: {
  state: SolarDesignDraftState;
  revision: number;
  original: SolarDesignRecoveryBaseline;
  readCurrent: () => Promise<SolarDesignRecoverySource>;
  save: (input: {
    state: SolarDesignDraftState;
    expectedRevision: number;
    sourceAnalysisId: string;
    sourceAnalysisVersion: string;
  }) => Promise<T>;
}) => {
  const original = input.original;
  if (!original.known) throw new Error(original.reason + KEPT);
  if (
    !input.state.layout.workingFaces.length ||
    !input.state.layout.workingPanels.length
  ) {
    throw new Error(
      'This layout has no editable roof and panel geometry to carry across. Your design has not been replaced.'
    );
  }
  const { source, site } = await input.readCurrent();
  if (
    !source?.available ||
    !source.centre ||
    !source.sourceAnalysisId ||
    !source.sourceAnalysisVersion ||
    !source.geometry?.panelDimensions
  ) {
    throw new Error(
      'The current roof analysis is unavailable. Your layout is still here and has not been replaced.'
    );
  }
  if (
    !sameSite(original.site, site) ||
    (original.sourceAnalysisId != null &&
      original.sourceAnalysisId !== source.sourceAnalysisId) ||
    (original.centre !== undefined &&
      (!original.centre ||
        original.centre.lat !== source.centre.lat ||
        original.centre.lng !== source.centre.lng))
  ) {
    throw new Error(SITE_CHANGED_MESSAGE + KEPT);
  }
  if (
    !sameDimensions(original.panelDimensions, source.geometry.panelDimensions)
  ) {
    throw new Error(PANEL_SIZE_CHANGED_MESSAGE + KEPT);
  }
  const saved = await input.save({
    state: input.state,
    expectedRevision: input.revision,
    sourceAnalysisId: source.sourceAnalysisId,
    sourceAnalysisVersion: source.sourceAnalysisVersion,
  });
  return { source, saved };
};
